/**
 * M3X.WORKSPACE F.4 — Rail 2 state hook with localStorage persistence.
 *
 * Per spec §0.5 + §6.1 — the 3-state machine:
 *   expanded → thin → hidden → expanded (cycle on `[`)
 *
 * Persists per user (the Clerk user id is passed in by the caller —
 * `useWorkspace()` F.5 will provide it from the Clerk session).
 */

import { useCallback, useEffect, useState } from "react";

import type { Rail2State } from "./Rail2";


const _STATES: Rail2State[] = ["expanded", "thin", "hidden"];


function _storageKey(userId: string | null): string {
  return `workspace:rail2-state:${userId ?? "anon"}`;
}


function _read(userId: string | null): Rail2State {
  if (typeof window === "undefined") return "expanded";
  try {
    const raw = window.localStorage.getItem(_storageKey(userId));
    if (raw === "thin" || raw === "hidden" || raw === "expanded") {
      return raw;
    }
  } catch {
    // Storage disabled (private mode) — fall through
  }
  return "expanded";
}


function _write(userId: string | null, state: Rail2State): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(_storageKey(userId), state);
  } catch {
    // ignore
  }
}


export interface UseRail2StateResult {
  rail2State: Rail2State;
  setRail2State: (next: Rail2State) => void;
  /** Cycle expanded → thin → hidden → expanded. */
  cycleRail2: () => void;
}


export function useRail2State(
  userId: string | null = null,
): UseRail2StateResult {
  const [state, setStateRaw] = useState<Rail2State>(() => _read(userId));

  // If the userId changes (login swap), re-read storage
  useEffect(() => {
    setStateRaw(_read(userId));
  }, [userId]);

  const setRail2State = useCallback(
    (next: Rail2State) => {
      setStateRaw(next);
      _write(userId, next);
    },
    [userId],
  );

  const cycleRail2 = useCallback(() => {
    const idx = _STATES.indexOf(state);
    const next = _STATES[(idx + 1) % _STATES.length];
    setRail2State(next);
  }, [state, setRail2State]);

  // Global `[` keyboard listener — cycle the rail
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Don't intercept in inputs / textareas / contenteditable
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      if (e.key === "[" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        cycleRail2();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [cycleRail2]);

  return { rail2State: state, setRail2State, cycleRail2 };
}
